import React, { useState, useEffect } from 'react';
import axios from 'axios';

const VouchModal = ({ 
  toUserId, 
  toUsername, 
  codeRequestId, 
  giveawayId, 
  gameName: defaultGameName,
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    gameName: defaultGameName || '',
    message: '',
    rating: 5
  });
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  const resolvedToUserId =
    toUserId ||
    (eligibility && eligibility.toUserId) ||
    '';

  useEffect(() => {
    checkEligibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkEligibility = async () => {
    try {
      setCheckingEligibility(true);
      const token = localStorage.getItem('token');
      const params = {};
      
      if (codeRequestId) params.codeRequestId = codeRequestId;
      if (giveawayId) params.giveawayId = giveawayId;
      if (!codeRequestId && !giveawayId) params.toUserId = toUserId;

      const response = await axios.get('/api/vouches/check-eligibility', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setEligibility(response.data);
      
      if (!response.data.eligible) {
        setError(response.data.reason);
      }
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setError(err.response?.data?.message || 'Failed to check eligibility');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleScreenshotChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Store the file object directly (no separate upload)
    setScreenshot(file);
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.gameName.trim()) {
      setError('Please enter the game name');
      return;
    }

    if (!formData.message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (formData.message.trim().length < 10) {
      setError('Message must be at least 10 characters');
      return;
    }

    if (!screenshot) {
      setError('Please upload a screenshot');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Create FormData to send file
      const formDataToSend = new FormData();
      if (!resolvedToUserId) {
        setError('Could not determine who to vouch for. Please refresh and try again.');
        setLoading(false);
        return;
      }

      console.log('Submitting vouch payload:', {
        toUserId: resolvedToUserId,
        codeRequestId: codeRequestId || null,
        giveawayId: giveawayId || null,
        gameName: formData.gameName.trim()
      });

      formDataToSend.append('toUserId', resolvedToUserId);
      formDataToSend.append('gameName', formData.gameName.trim());
      formDataToSend.append('message', formData.message.trim());
      formDataToSend.append('rating', parseInt(formData.rating));
      if (codeRequestId) formDataToSend.append('codeRequestId', codeRequestId);
      if (giveawayId) formDataToSend.append('giveawayId', giveawayId);
      
      // Append the screenshot file (not the URL)
      formDataToSend.append('screenshot', screenshot);

      await axios.post('/api/vouches', formDataToSend, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess();
    } catch (err) {
      console.error('Error creating vouch:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      setError(err.response?.data?.message || 'Failed to create vouch');
    } finally {
      setLoading(false);
    }
  };

  if (checkingEligibility) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content vouch-modal" onClick={(e) => e.stopPropagation()}>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Checking eligibility...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content vouch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⭐ Vouch for {toUsername}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {eligibility && !eligibility.eligible ? (
          <div className="eligibility-error">
            <p className="error-icon">❌</p>
            <h3>Cannot Vouch</h3>
            <p>{error}</p>
            <button onClick={onClose} className="btn-primary">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="vouch-form">
            {eligibility && eligibility.eligible && (
              <div className={eligibility.verified === false ? "eligibility-warning" : "eligibility-success"}>
                <p className="success-icon">{eligibility.verified === false ? '⚠️' : '✅'}</p>
                <p className="success-message">
                  {eligibility.reason}
                  {eligibility.verified === false && (
                    <span style={{ display: 'block', fontSize: '0.9rem', marginTop: '8px', opacity: 0.8 }}>
                      This will be an unverified vouch since it's not linked to a specific code or giveaway win.
                    </span>
                  )}
                </p>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {/* Game Name */}
            <div className="form-group">
              <label>
                Game Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="gameName"
                value={formData.gameName}
                onChange={handleChange}
                placeholder="Enter the game name"
                required
                disabled={defaultGameName && defaultGameName.trim()}
              />
            </div>

            {/* Rating */}
            <div className="form-group">
              <label>
                Rating <span className="required">*</span>
              </label>
              <div className="rating-selector">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`star-btn ${formData.rating >= star ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, rating: star })}
                  >
                    ⭐
                  </button>
                ))}
              </div>
              <small>Rate your experience (1-5 stars)</small>
            </div>

            {/* Message */}
            <div className="form-group">
              <label>
                Message <span className="required">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Share your experience... Did you receive the game? Was the process smooth? Would you recommend this person?"
                rows="5"
                maxLength="500"
                required
              />
              <small>{formData.message.length}/500 characters (minimum 10)</small>
            </div>

            {/* Screenshot Upload */}
            <div className="form-group">
              <label>
                Proof Screenshot <span className="required">*</span>
              </label>
              <small className="screenshot-info">
                Upload a screenshot showing you received/redeemed the game. This helps verify vouches and builds trust.
              </small>
              
              {!screenshotPreview ? (
                <div className="screenshot-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    id="screenshot-input"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="screenshot-input" className="upload-label">
                    <span className="upload-icon">📸</span>
                    <span>Click to upload screenshot</span>
                    <small>PNG, JPG or GIF (Max 5MB)</small>
                  </label>
                </div>
              ) : (
                <div className="screenshot-preview">
                  <img src={screenshotPreview} alt="Vouch proof" />
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    className="remove-screenshot"
                  >
                    ✕ Remove
                  </button>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="info-box">
              <p>
                <strong>📝 Vouch Guidelines:</strong>
              </p>
              <ul>
                <li>Only vouch if you actually received the game/code</li>
                <li>Upload proof that shows you received/redeemed the game</li>
                <li>Be honest about your experience</li>
                <li>Vouches and screenshots are publicly visible</li>
                <li>False vouches may result in account action</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || (eligibility && !eligibility.eligible)}
              >
                {loading ? 'Submitting...' : '⭐ Submit Vouch'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default VouchModal;
